import { type ReactNode, type Ref, useCallback, useState } from 'react'
import { type Observable } from 'rxjs'
import { MagicBorder } from '@/components/MagicBorder'
import {
  ScreenHeaderProvider,
  ScreenHeaderContent,
  ScreenHeaderMain,
  ScreenHeaderPersistentTopBar,
  ScreenHeaderTitle,
  useScreenHeaderTotalOffset,
  useTopBarElementScrollProgress,
} from '@/components/ScreenHeader'
import { TextField, TextFieldSlot } from '@/components/inputs/TextField'
import { Icon } from '@/components/Icon'
import {
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@/components/Table'
import {
  type MotionValue,
  m,
  useMotionValueEvent,
  useTransform,
  useScroll,
  useMotionValue,
} from 'motion/react'
import { useObservableValue, useSubject } from '@/hooks/rx-hooks'
import { useProvideScrollPaddingTop } from '@/contexts/scroll-padding-context'
import { type ElementSize, useElementSize } from '@/hooks/element-hooks'
import { usePageTopOffset } from '@/contexts/page-top-offset-context'
import { DocumentThumbnail } from '@/features/docs/components/DocumentThumbnail'
import { useSkeletonListContext } from '@/components/Skeleton/SkeletonList'
import { Skeleton } from '@/components/Skeleton'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import type { FocusChangeEventHandler } from '@/infra/prop-types'
import { mediaQueryMatchStream } from '@/utils/browser-util'
import { dateFormatters } from '@/utils/date-util'
import {
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateIconDecoration,
  EmptyStateTitle,
} from '@/components/EmptyState'
import { Button } from '@/components/Button'
import { links } from '@/utils/link'
import { SearchField } from 'react-aria-components'
import { IconButton } from '@/components/IconButton'
import styles from './DocumentsView.module.css'

export type DocumentsViewEmptyState =
  | 'no-documents'
  | 'no-search-results'
  | null

interface DocumentsViewProps {
  searchTextValue: string
  onSearchTextChange: (newValue: string) => unknown
  onClearSearchText: () => unknown
  emptyState?: DocumentsViewEmptyState
  children?: ReactNode
}

/**
 * The DocumentsView
 */
export function DocumentsView({
  searchTextValue,
  onSearchTextChange,
  onClearSearchText,
  emptyState,
  children,
}: DocumentsViewProps) {
  const isSpacious = useIsSpacious()

  return (
    <>
      {isSpacious ? (
        <SpaciousDocumentsViewLayout
          searchTextValue={searchTextValue}
          onSearchTextChange={onSearchTextChange}
          onClearSearchText={onClearSearchText}
          emptyState={emptyState}
        >
          {children}
        </SpaciousDocumentsViewLayout>
      ) : (
        <ScreenHeaderProvider isOverlay>
          <CompactDocumentsViewLayout
            searchTextValue={searchTextValue}
            onSearchTextChange={onSearchTextChange}
            onClearSearchText={onClearSearchText}
            emptyState={emptyState}
          >
            {children}
          </CompactDocumentsViewLayout>
        </ScreenHeaderProvider>
      )}
    </>
  )
}

interface DocumentsViewHeaderProps {
  children?: ReactNode
  inlineSearchFieldOpacity?: MotionValue<number>
}

/**
 * Documents View Header
 */
function DocumentsViewHeader({
  inlineSearchFieldOpacity,
  children,
}: DocumentsViewHeaderProps) {
  return (
    <header className={styles.header}>
      <div className={styles.headerInfo}>
        <h1 className="text-display-lg">Documents</h1>
        <p className="text-body-lg mt-sm">Manage your document collection</p>
      </div>

      {children && (
        <m.div
          className={styles.headerSearchFieldContainer}
          style={{ opacity: inlineSearchFieldOpacity }}
        >
          {children}
        </m.div>
      )}
    </header>
  )
}

interface SearchTextFieldProps {
  value: string
  onChange: (newValue: string) => unknown
  onClear: () => unknown
  onArrowDown: () => unknown
  onFocusChange?: FocusChangeEventHandler
  isEmbedded?: boolean
  inert?: boolean
  focus$?: Observable<boolean>
}

/**
 * Search Text Field
 */
function SearchTextField({
  value,
  onChange,
  onClear,
  onFocusChange,
  onArrowDown,
  isEmbedded,
  inert,
  focus$,
}: SearchTextFieldProps) {
  return (
    <SearchField value={value} onChange={onChange} onClear={onClear}>
      <TextField
        placeholder="Search"
        variant={isEmbedded ? 'embedded' : 'default'}
        inert={inert}
        value={value}
        onChange={onChange}
        onFocusChange={onFocusChange}
        focus$={focus$}
        aria-label="Search"
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            onArrowDown()
          }
        }}
      >
        <TextFieldSlot>
          <Icon name="search" />
        </TextFieldSlot>
        {value && (
          <TextFieldSlot side="right">
            <IconButton name="close" onPress={onClear} label="Clear search" />
          </TextFieldSlot>
        )}
      </TextField>
    </SearchField>
  )
}

interface SpaciousDocumentsViewLayoutProps {
  searchTextValue: string
  onSearchTextChange: (newValue: string) => unknown
  onClearSearchText: () => unknown
  emptyState?: DocumentsViewEmptyState
  children?: ReactNode
}

/**
 * Layout for spacious screens.
 */
function SpaciousDocumentsViewLayout({
  searchTextValue,
  onClearSearchText,
  onSearchTextChange,
  emptyState,
  children,
}: SpaciousDocumentsViewLayoutProps) {
  // Used to trigger focus on the first table row when hitting down arrow.
  const focusFirst$ = useSubject()
  const tableHeaderWidth = useMotionValue(0)
  const tableHeaderHeight = useMotionValue(0)

  const { scrollY, scrollYProgress } = useScroll()

  const { connectRef: connectHeaderRef, size: headerSize } = useElementSize()
  const { connectRef: connectTableHeaderRef, size: tableHeaderSize } =
    useElementSize({
      onChange: useCallback(
        (s: ElementSize) => {
          tableHeaderWidth.set(s.width)
          tableHeaderHeight.set(s.height)
        },
        [tableHeaderHeight, tableHeaderWidth],
      ),
    })

  // The wider the screen, the further the scroll-end should be so the
  // border doesn't animate too quickly.
  const superWide = useObservableValue(() =>
    mediaQueryMatchStream('screen and (min-width: 1900px)'),
  )
  const scrollEnd = superWide ? 100 : 50
  const scrollProgress = useTransform(scrollY, [10, scrollEnd], [0, 1])

  useProvideScrollPaddingTop(headerSize.height + tableHeaderSize.height)

  // Make sure we start at zero if there's scroll bar.
  // We also make sure that we never end up in a situation where
  // the animation won't be able to reach its end state.
  const animationProgress = useTransform(() =>
    scrollY.get() === 0
      ? 0
      : Math.max(scrollProgress.get(), scrollYProgress.get()),
  )

  return (
    <>
      <div className={styles.stickyHeader} ref={connectHeaderRef}>
        <div className="max-w-content mx-auto">
          <DocumentsViewHeader>
            {emptyState !== 'no-documents' && (
              <SearchTextField
                value={searchTextValue}
                onChange={onSearchTextChange}
                onClear={onClearSearchText}
                onArrowDown={() => focusFirst$.next(undefined)}
              />
            )}
          </DocumentsViewHeader>
        </div>
      </div>

      {!emptyState && (
        <MagicBorder
          progress={animationProgress}
          contentWidth={tableHeaderWidth}
          contentHeight={tableHeaderHeight}
          offset={headerSize.height}
        />
      )}

      <main className="px-7xl">
        <div className="max-w-content mx-auto">
          {emptyState === 'no-documents' ? (
            <NoDocuments />
          ) : (
            <DocumentsTable
              focusFirst$={focusFirst$}
              isHeaderBorderless={emptyState === 'no-search-results'}
              tableHeaderRef={connectTableHeaderRef}
              tableHeaderStickyOffset={headerSize.height}
            >
              {children}
            </DocumentsTable>
          )}
        </div>
      </main>
    </>
  )
}

interface CompactDocumentsViewLayoutProps {
  searchTextValue: string
  onSearchTextChange: (newValue: string) => unknown
  onClearSearchText: () => unknown
  emptyState?: DocumentsViewEmptyState
  children?: ReactNode
}

/**
 * Layout for compact screens.
 */
function CompactDocumentsViewLayout({
  searchTextValue,
  onClearSearchText,
  onSearchTextChange,
  emptyState,
  children,
}: CompactDocumentsViewLayoutProps) {
  useProvideScrollPaddingTop(112)
  const focusFirst$ = useSubject()
  const [isUsingSearchBar, setIsUsingSearchBar] = useState(false)
  const [inlineSearchFocused, setInlineSearchFocused] = useState(false)
  const [searchBarFocused, setSearchBarFocused] = useState(false)
  const triggerInlineSearchFocus$ = useSubject<boolean>()
  const triggerSearchBarFocus$ = useSubject<boolean>()
  const searchBarOpacity = useTopBarElementScrollProgress('content', -32)
  const topBarOffset = usePageTopOffset()

  const inlineSearchFieldOpacity = useTransform(
    useTopBarElementScrollProgress('content', -80),
    [0.5, 1],
    [1, 0],
  )

  // As the search bar fades, we'll swap between the search bar and search field
  // and transfer focus between them accordingly.
  useMotionValueEvent(
    useTransform(searchBarOpacity, [0, 0.2], [false, true]),
    'change',
    useCallback(
      (isUsingSearchBar: boolean) => {
        setIsUsingSearchBar(isUsingSearchBar)

        // Since the above causes a rerender, we have to wait for
        // the next paint to transfer focus.
        if (isUsingSearchBar && inlineSearchFocused) {
          requestAnimationFrame(() => triggerSearchBarFocus$.next(true))
          return
        }

        if (!isUsingSearchBar && searchBarFocused) {
          requestAnimationFrame(() => triggerInlineSearchFocus$.next(true))
        }
      },
      [
        inlineSearchFocused,
        searchBarFocused,
        triggerInlineSearchFocus$,
        triggerSearchBarFocus$,
      ],
    ),
  )

  return (
    <>
      <ScreenHeaderPersistentTopBar>
        <div className={styles.topBarFiller} />
      </ScreenHeaderPersistentTopBar>
      <ScreenHeaderMain>
        <ScreenHeaderContent>
          <ScreenHeaderTitle>
            <DocumentsViewHeader
              inlineSearchFieldOpacity={inlineSearchFieldOpacity}
            >
              {emptyState !== 'no-documents' && (
                <SearchTextField
                  value={searchTextValue}
                  onChange={onSearchTextChange}
                  onClear={onClearSearchText}
                  onFocusChange={setInlineSearchFocused}
                  focus$={triggerInlineSearchFocus$}
                  inert={isUsingSearchBar}
                  onArrowDown={() => focusFirst$.next(undefined)}
                />
              )}
            </DocumentsViewHeader>
          </ScreenHeaderTitle>
        </ScreenHeaderContent>
      </ScreenHeaderMain>
      {emptyState !== 'no-documents' ? (
        <>
          <m.div
            className={styles.compactSearchBar}
            style={{
              top: topBarOffset,
              opacity: searchBarOpacity,
            }}
          >
            <SearchTextField
              isEmbedded
              inert={!isUsingSearchBar}
              value={searchTextValue}
              onChange={onSearchTextChange}
              onClear={onClearSearchText}
              onFocusChange={setSearchBarFocused}
              focus$={triggerSearchBarFocus$}
              onArrowDown={() => focusFirst$.next(undefined)}
            />
          </m.div>
          <CompactDocumentsTable
            focusFirst$={focusFirst$}
            emptyState={emptyState}
          >
            {children}
          </CompactDocumentsTable>
        </>
      ) : (
        <div className="pt-base px-3xl">
          <NoDocuments />
        </div>
      )}
    </>
  )
}

interface CompactDocumentsTableProps {
  emptyState?: DocumentsViewEmptyState
  focusFirst$: Observable<unknown>
  children?: ReactNode
}

/**
 * Documents table for use with compact screens.
 */
function CompactDocumentsTable({
  emptyState,
  focusFirst$,
  children,
}: CompactDocumentsTableProps) {
  const offset = useScreenHeaderTotalOffset()
  const scrollProgress = useTopBarElementScrollProgress('content', -20)

  const {
    connectRef: connectTableHeaderRef,
    sizeMotionValues: { width: tableHeaderWidth, height: tableHeaderHeight },
  } = useElementSize()

  return (
    <main>
      {!emptyState && (
        <MagicBorder
          progress={scrollProgress}
          contentHeight={tableHeaderHeight}
          contentWidth={tableHeaderWidth}
          offset={offset}
        />
      )}

      <div className={styles.tableContainer}>
        <DocumentsTable
          tableHeaderRef={connectTableHeaderRef}
          tableHeaderStickyOffset={offset}
          focusFirst$={focusFirst$}
          isHeaderBorderless={!!emptyState}
        >
          {children}
        </DocumentsTable>
      </div>
    </main>
  )
}

interface DocumentsTableProps {
  tableHeaderRef?: Ref<HTMLTableSectionElement>
  tableHeaderStickyOffset?: number | string
  focusFirst$: Observable<unknown>
  children?: ReactNode
  isHeaderBorderless?: boolean
}

/**
 * Documents Table
 */
function DocumentsTable({
  tableHeaderRef,
  tableHeaderStickyOffset,
  focusFirst$,
  children,
  isHeaderBorderless,
}: DocumentsTableProps) {
  const isSpacious = useIsSpacious()
  return (
    <Table trailingSpacing aria-label="Documents" rowSpacing="base">
      <TableHeader
        ref={tableHeaderRef}
        sticky={tableHeaderStickyOffset}
        isBorderless={isHeaderBorderless}
      >
        <TableColumn minWidth={150} isRowHeader>
          Name
        </TableColumn>
        {isSpacious && (
          <>
            <TableColumn minWidth={200}>Job</TableColumn>
            <TableColumn minWidth={150}>Type</TableColumn>
            <TableColumn minWidth={150}>Date created</TableColumn>
          </>
        )}
        <TableColumn isRowActionsHeader />
      </TableHeader>
      <TableBody focusFirst$={focusFirst$} emptyState={<NoSearchResults />}>
        {children}
      </TableBody>
    </Table>
  )
}

interface DocumentTableRowProps {
  isDisabled?: boolean
  title?: string
  isJobNamesSkeleton?: boolean
  isUserSkeleton?: boolean
  isTypeImageSkeleton?: boolean
  isTypeNameSkeleton?: boolean
  createdAt?: Date
  authorName?: string | null
  typeName?: string | null
  typeImageUrl?: string | null
  jobNames?: string[]
  actions?: ReactNode
}

/**
 * Document Table Row
 */
export function DocumentTableRow({
  isDisabled,
  title,
  isJobNamesSkeleton,
  isUserSkeleton,
  isTypeNameSkeleton,
  isTypeImageSkeleton,
  typeName,
  typeImageUrl,
  authorName,
  jobNames = [],
  createdAt,
  actions,
}: DocumentTableRowProps) {
  const { ref } = useSkeletonListContext()
  const { vary } = useSkeletonListContext()
  const isSpacious = useIsSpacious()

  return (
    <TableRow
      ref={ref as Ref<HTMLTableRowElement>}
      isDisabled={isDisabled}
      textValue={title || 'Placeholder'}
    >
      <TableCell>
        <div className="flex gap-lg items-center">
          <div>
            <DocumentThumbnail
              isSkeleton={isTypeImageSkeleton}
              name={typeName}
              imageUrl={typeImageUrl}
            />
          </div>

          <div>
            <p className={styles.documentTitle} title={title}>
              {title ?? <Skeleton width={vary(isSpacious ? 200 : 80)} />}
            </p>
            {!isSpacious && (
              <p className={styles.documentType} title={typeName ?? undefined}>
                {!isTypeNameSkeleton ? (
                  typeName
                ) : (
                  <Skeleton width={vary(isSpacious ? 140 : 40)} />
                )}
              </p>
            )}
          </div>
        </div>
      </TableCell>
      {isSpacious && (
        <>
          <TableCell>
            {!isJobNamesSkeleton ? (
              <span className={styles.jobName}>{jobNames.join(', ')}</span>
            ) : (
              <Skeleton width={vary(120)} />
            )}
          </TableCell>
          <TableCell>
            {!isTypeNameSkeleton ? typeName : <Skeleton width={vary(100)} />}
          </TableCell>
          <TableCell>
            {!isUserSkeleton && createdAt ? (
              <>
                {dateFormatters.dateMedium.format(createdAt)}

                {authorName ? (
                  <>
                    &nbsp;&nbsp;
                    {authorName}
                  </>
                ) : null}
              </>
            ) : (
              <Skeleton width={vary(100)} />
            )}
          </TableCell>
        </>
      )}
      <TableCell>{actions}</TableCell>
    </TableRow>
  )
}

/**
 * No Documents
 */
function NoDocuments() {
  return (
    <div className="mt-2xl">
      <EmptyState size="lg">
        <EmptyStateIconDecoration icon="file" />
        <EmptyStateTitle>No documents are available</EmptyStateTitle>
        <EmptyStateDescription>
          Submit a consultation, bookkeeping, or tax filing request to get
          started. All documents added from those requests will appear here.
        </EmptyStateDescription>
        <EmptyStateActions>
          <Button label="Get started today" to={links.startNewService()} />
        </EmptyStateActions>
      </EmptyState>
    </div>
  )
}

/**
 * No Documents
 */
function NoSearchResults() {
  return (
    <EmptyState size="lg">
      <EmptyStateIconDecoration icon="alert" />
      <EmptyStateTitle>No results found</EmptyStateTitle>
      <EmptyStateDescription>
        Try using different keywords or search terms.
      </EmptyStateDescription>
    </EmptyState>
  )
}
